import {Form, Formik, useFormikContext} from 'formik';
import {flushSync} from 'react-dom';
import {FormattedMessage, useIntl} from 'react-intl';
import {Navigate, useNavigate} from 'react-router';
import {useUpdateEffect} from 'react-use';
import {z} from 'zod';
import {toFormikValidationSchema} from 'zod-formik-adapter';

import {CardTitle} from '@/components/Card';
import useTitle from '@/hooks/useTitle';

import {useCreateAppointmentContext} from '../CreateAppointment/CreateAppointmentState';
import SubmitRow from '../SubmitRow';
import {DateSelect, LocationSelect, TimeSelect} from '../fields';
import {ProductSummary} from '../summary/ProductSummary';

export interface LocationAndTimeStepValues {
  location: string;
  date: string;
  datetime: string;
}

const schema = z.object({
  location: z.string(),
  date: z.coerce.date(),
  datetime: z.string().datetime({offset: true}),
});

// XXX: check field dependencies - clear time if date/location is not valid etc.

const INITIAL_VALUES: LocationAndTimeStepValues = {
  location: '',
  date: '',
  datetime: '',
};

const LocationAndTimeStepFields = () => {
  const intl = useIntl();
  const {values, setFieldValue} = useFormikContext<LocationAndTimeStepValues>();
  const {
    appointmentData: {products = []},
  } = useCreateAppointmentContext<'kalender'>();

  const {location, date} = values;
  // if a field changes, clear the dependent fields
  useUpdateEffect(() => {
    setFieldValue('date', '');
    setFieldValue('datetime', '');
  }, [location]);
  useUpdateEffect(() => {
    setFieldValue('datetime', '');
  }, [date]);

  // if we don't have products in the state, redirect back to the start
  if (!products.length) {
    return <Navigate to="/" replace />;
  }

  return (
    // TODO: don't do inline style
    <Form style={{width: '100%'}}>
      <div className="openforms-form-field-container">
        <LocationSelect products={products} />
        <DateSelect products={products} />
        <TimeSelect products={products} />
      </div>

      <SubmitRow
        canSubmit
        nextText={intl.formatMessage({
          description: 'Appointments location and time step: next step text',
          defaultMessage: 'To contact details',
        })}
        previousText={intl.formatMessage({
          description: 'Appointments location and time step: previous step text',
          defaultMessage: 'Back to products',
        })}
        navigateBackTo="producten"
      />
    </Form>
  );
};

export interface LocationAndTimeStepProps {
  navigateTo?: string;
}

const LocationAndTimeStep: React.FC<LocationAndTimeStepProps> = ({navigateTo = ''}) => {
  const intl = useIntl();
  const {
    appointmentData: {products = []},
    stepData,
    stepErrors: {initialErrors, initialTouched},
    clearStepErrors,
    submitStep,
  } = useCreateAppointmentContext<'kalender'>();
  const navigate = useNavigate();
  useTitle(
    intl.formatMessage({
      description: 'Appointments: date and time step step page title',
      defaultMessage: 'Date and time',
    })
  );

  return (
    <>
      <CardTitle
        title={
          <FormattedMessage
            description="Appointments: select date and time step title"
            defaultMessage="Date and time"
          />
        }
        headingType="subtitle"
        padded
      />

      <ProductSummary products={products} />

      <Formik<LocationAndTimeStepValues>
        initialValues={{...INITIAL_VALUES, ...stepData}}
        initialErrors={initialErrors}
        initialTouched={initialTouched}
        validateOnChange={false}
        validateOnBlur={false}
        validationSchema={toFormikValidationSchema(schema)}
        onSubmit={(values, {setSubmitting}) => {
          flushSync(() => {
            clearStepErrors();
            submitStep(values);
            setSubmitting(false);
          });
          if (navigateTo) navigate(navigateTo);
        }}
        component={LocationAndTimeStepFields}
      />
    </>
  );
};

export default LocationAndTimeStep;
